import { useState } from 'react';
import type { QAReport } from '../types/qa-report';

interface Props {
  report: QAReport;
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  pass: { bg: 'rgba(16,185,129,0.12)', color: '#10b981' },
  fail: { bg: 'rgba(239,68,68,0.12)', color: '#ef4444' },
  skip: { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b' },
};

const VERDICT_STYLES: Record<string, { bg: string; border: string; color: string }> = {
  PASS: { bg: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.3)', color: '#10b981' },
  CONDITIONAL: { bg: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', color: '#f59e0b' },
  FAIL: { bg: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444' },
};

export function isQAReport(value: unknown): value is QAReport {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    Array.isArray(v.suites) &&
    typeof v.verdict === 'string' &&
    ['PASS', 'CONDITIONAL', 'FAIL'].includes(v.verdict) &&
    typeof v.summaryStats === 'object' &&
    v.summaryStats !== null &&
    typeof (v.summaryStats as Record<string, unknown>).totalTests === 'number'
  );
}

export default function QAReportView({ report }: Props) {
  const [expandedSuites, setExpandedSuites] = useState<Record<string, boolean>>({});
  const [showCode, setShowCode] = useState(false);

  const { summaryStats, verdict, verdictRationale, suites, testStrategy, playwrightCode } = report;
  const verdictStyle = VERDICT_STYLES[verdict] ?? VERDICT_STYLES.FAIL;
  const coverageMet = summaryStats.coveragePercent >= summaryStats.coverageTarget;

  const toggleSuite = (name: string) => {
    setExpandedSuites(prev => ({ ...prev, [name]: !prev[name] }));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Verdict Banner */}
      <div style={{ padding: '16px 20px', borderRadius: 'var(--radius-md)', ...verdictStyle }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: verdictStyle.color }}>
            Verdict: {verdict}
          </span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-secondary)' }}>
            {report.generatedAt ? new Date(report.generatedAt).toLocaleString() : ''}
          </span>
        </div>
        {verdictRationale && (
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginTop: 6 }}>
            {verdictRationale}
          </p>
        )}
      </div>

      {/* Summary Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12 }}>
        {[
          { label: 'Total', value: summaryStats.totalTests, color: 'var(--text)' },
          { label: 'Passed', value: summaryStats.passed, color: '#10b981' },
          { label: 'Failed', value: summaryStats.failed, color: '#ef4444' },
          { label: 'Skipped', value: summaryStats.skipped, color: '#f59e0b' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ padding: '12px 16px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color }}>{value}</div>
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Coverage Bar */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '12px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Coverage</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: coverageMet ? '#10b981' : '#ef4444' }}>
            {summaryStats.coveragePercent}% / {summaryStats.coverageTarget}% target
          </span>
        </div>
        <div style={{ height: 8, background: 'rgba(255,255,255,0.06)', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${Math.min(summaryStats.coveragePercent, 100)}%`,
            background: coverageMet ? '#10b981' : '#ef4444',
            borderRadius: 4,
            transition: 'width 300ms ease',
          }} />
        </div>
      </div>

      {/* Test Strategy */}
      {testStrategy && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: '12px 16px' }}>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Test Strategy</div>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', margin: 0 }}>{testStrategy}</p>
        </div>
      )}

      {/* Test Suites */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Test Suites ({suites.length})
        </div>
        {suites.map((suite) => {
          const expanded = expandedSuites[suite.name] ?? false;
          const passed = suite.tests.filter(t => t.status === 'pass').length;
          const failed = suite.tests.filter(t => t.status === 'fail').length;
          const skipped = suite.tests.filter(t => t.status === 'skip').length;
          return (
            <div key={suite.name} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
              <button
                onClick={() => toggleSuite(suite.name)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 16px', background: 'transparent', border: 'none', cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 3, background: 'rgba(124,58,237,0.10)', color: 'var(--primary)', textTransform: 'uppercase' }}>
                    {suite.type}
                  </span>
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                    {suite.name}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {passed > 0 && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#10b981' }}>{passed} pass</span>}
                  {failed > 0 && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#ef4444' }}>{failed} fail</span>}
                  {skipped > 0 && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#f59e0b' }}>{skipped} skip</span>}
                  <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{expanded ? '▾' : '▸'}</span>
                </div>
              </button>
              {expanded && (
                <div style={{ borderTop: '1px solid var(--border)' }}>
                  {suite.tests.map((test, idx) => {
                    const sc = STATUS_COLORS[test.status] ?? STATUS_COLORS.skip;
                    return (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', borderBottom: idx < suite.tests.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 3, background: sc.bg, color: sc.color, textTransform: 'uppercase', flexShrink: 0 }}>
                          {test.status}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{test.name}</div>
                          <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{test.criterion}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Playwright Code */}
      {playwrightCode && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
          <button
            onClick={() => setShowCode(!showCode)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 16px', background: 'transparent', border: 'none', cursor: 'pointer',
            }}
          >
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
              Playwright Test Code
            </span>
            <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{showCode ? '▾' : '▸'}</span>
          </button>
          {showCode && (
            <pre style={{
              fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text)',
              background: 'var(--surface-alt)', padding: 16, margin: 0,
              borderTop: '1px solid var(--border)', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              maxHeight: 400, overflow: 'auto',
            }}>
              {playwrightCode}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
